import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const isEmail = (s: string) => z.string().email().safeParse(s).success;

/**
 * Editor for a list of bare email addresses (no names) — a contact's CC list,
 * or an invoice's To and CC recipients. Each address is validated as it's
 * committed (Enter, comma, or blur) so the bound `value` array is always
 * well-formed: blanks are ignored, invalid addresses surface an inline error,
 * and case-insensitive duplicates are dropped silently.
 *
 * With `addLabel`, the input stays behind a "+ {addLabel}" button until
 * someone adds an address, and closes again once it is committed — the list
 * reads as chips, not as an empty box waiting to be filled. `open` /
 * `onOpenChange` let a caller open it from elsewhere (e.g. an "Add email"
 * fix link); left out, the field tracks that itself.
 */
export function CcEmailsField({
  value,
  onChange,
  id = 'cc-emails',
  label = 'CC emails',
  description = 'Copied on this contact’s invoice emails. No names — just addresses.',
  disabled = false,
  addLabel,
  open,
  onOpenChange,
  error: externalError,
  onDraftChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  /** Distinct per mount, so two copies on one page don't share a label. */
  id?: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  addLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** A message from outside, e.g. the server rejecting a stored address. */
  error?: string;
  /**
   * The text still in the input, not yet an address in `value` — empty once
   * committed. Lets a caller refuse to act while an address is half-typed or
   * invalid, rather than quietly leave it out.
   */
  onDraftChange?: (draft: string) => void;
}) {
  const [draft, setDraftState] = useState('');
  const setDraft = (next: string) => {
    setDraftState(next);
    onDraftChange?.(next);
  };
  const [error, setError] = useState<string | null>(null);
  const [openState, setOpenState] = useState(false);
  const adding = open ?? openState;
  const setAdding = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    else setOpenState(next);
  };
  const showInput = addLabel === undefined || adding;

  const close = () => {
    setDraft('');
    setError(null);
    setAdding(false);
  };

  const commit = (raw: string) => {
    const email = raw.trim();
    if (!email) {
      if (addLabel !== undefined) close();
      return true;
    }
    if (!isEmail(email)) {
      setError('Enter a valid email address');
      return false;
    }
    if (!value.some((e) => e.toLowerCase() === email.toLowerCase())) {
      onChange([...value, email]);
    }
    setDraft('');
    setError(null);
    if (addLabel !== undefined) setAdding(false);
    return true;
  };

  const remove = (email: string) => onChange(value.filter((e) => e !== email));
  const shownError = error ?? externalError;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {/* Empty when the surrounding section already says what the list is for. */}
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      {value.length > 0 || (!showInput && !disabled) ? (
        <ul className="flex flex-wrap items-center gap-1.5 pt-0.5">
          {value.map((email) => (
            <li
              key={email}
              className="flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-sm"
            >
              <span>{email}</span>
              {/* Gone rather than greyed when the list is locked: a disabled ×
                  still reads as "this can be removed". */}
              {disabled ? null : (
                <button
                  type="button"
                  aria-label={`Remove ${email}`}
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => remove(email)}
                >
                  <X className="size-3.5" />
                </button>
              )}
            </li>
          ))}
          {!showInput && !disabled ? (
            // Flex, like the chip items: a plain <li> lays the button out on a
            // text baseline, and the extra line-box height pushes it off centre.
            <li className="flex">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 gap-1 rounded px-2 py-0.5 font-normal text-muted-foreground text-sm"
                onClick={() => setAdding(true)}
              >
                <Plus className="size-3.5" />
                {addLabel}
              </Button>
            </li>
          ) : null}
        </ul>
      ) : null}
      {showInput ? (
        <Input
          id={id}
          type="email"
          disabled={disabled}
          placeholder="name@example.com"
          value={draft}
          autoFocus={addLabel !== undefined}
          aria-invalid={!!shownError}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit(draft);
            } else if (e.key === 'Escape' && addLabel !== undefined) {
              e.preventDefault();
              close();
            }
          }}
          onBlur={() => commit(draft)}
        />
      ) : null}
      {shownError ? <p className="text-xs text-destructive">{shownError}</p> : null}
    </div>
  );
}
